<template>
  <div>
    <div v-if="!hideDateFilter" class="d-flex justify-end mb-4">
      <DateTimeFilter
        v-model="dateRange"
        :ranges="['Today', '2d', '1w', '1m']"
        default="1m"
        custom
        class="black-border d-flex"
        :time-zone="facilityTimeZone"
      />
    </div>
    <EventVideoDialog
      v-model="eventsDialog"
      :thumbnail="currentThumbnail"
      @closed="handleDialogClosed"
    />

    <div v-if="loading" class="mt-4">
      <v-row>
        <v-col
          v-for="n in itemsPerPage"
          :key="`skeleton-${n}`"
          class="px-1 py-1"
          :cols="gridCols.cols"
          :sm="gridCols.sm"
          :md="gridCols.md"
        >
          <v-skeleton-loader
            type="card, image"
            class="mx-auto"
            :height="getCardHeight()"
          ></v-skeleton-loader>
        </v-col>
      </v-row>
    </div>

    <div
      v-else-if="thumbnails.length === 0 && allRequestsLoaded"
      class="text-center grey--text mt-4"
      height="230px"
    >
      <div>
        <p class="mb-0">
          No incidents to display.
          <span v-if="paginationTracking.hasMore.newer">
            Return to previous page
          </span>
        </p>
      </div>
    </div>

    <div v-else class="mt-4">
      <v-row class="video-grid-row">
        <v-col
          v-for="(thumbnail, index) in visibleThumbnails"
          :key="`thumbnail-${thumbnail.id}-${index}`"
          class="video-grid-col"
          :cols="gridCols.cols"
          :sm="gridCols.sm"
          :md="gridCols.md"
        >
          <v-card
            elevation="2"
            class="thumbnail-card"
            :class="{ 'compact-card': displayMode === 'compact' }"
          >
            <div
              class="position-relative video-container"
              :style="{
                height: getCardHeight(),
              }"
            >
              <v-img
                v-if="thumbnail.thumbnail"
                :src="'data:image/jpeg;base64,' + thumbnail.thumbnail"
                :height="getCardHeight()"
                cover
                position="top"
                class="grey lighten-3 video-image"
                @click="showEventsDialog(thumbnail)"
              >
                <template v-slot:placeholder>
                  <v-skeleton-loader
                    type="image"
                    :height="getCardHeight()"
                  ></v-skeleton-loader>
                </template>

                <!-- Play button overlay -->
                <div class="video-overlay d-flex justify-center align-center">
                  <v-btn icon color="white" class="play-button">
                    <v-icon large>fas fa-play-circle</v-icon>
                  </v-btn>
                </div>

                <!-- Text overlay that's always visible and fades down on hover -->
                <div class="text-overlay-always-visible">
                  <div class="text-content">
                    <div class="zone-name">
                      <a
                        class="clickable-link"
                        @click.stop="gotoZone(thumbnail?.organization?.id)"
                      >
                        {{ thumbnail.zoneName }}
                      </a>
                    </div>
                    <div class="incident-status">
                      {{ thumbnail.incidentType }}
                    </div>
                    <div class="timestamp">
                      <a
                        class="clickable-link"
                        @click.stop="gotoIncidentPage(thumbnail)"
                      >
                        {{ formatTimestamp(thumbnail.startTimestamp) }}
                      </a>
                    </div>
                    <!-- <div v-if="showLocation" class="location">
                      <v-icon small class="mr-1">mdi mdi-garage-variant</v-icon>
                      {{ thumbnail.deviceLocation || "Location not available" }}
                    </div> -->
                  </div>
                </div>
              </v-img>

              <v-skeleton-loader
                v-else-if="thumbnail.isLoading"
                type="image"
                :height="getCardHeight()"
                class="grey lighten-3"
              ></v-skeleton-loader>

              <div
                v-else
                class="grey lighten-3 d-flex justify-center align-center fill-height"
                :style="{ height: getCardHeight() }"
              >
                <div class="no-video-text pa-2">
                  <v-icon left color="error" :small="displayMode === 'compact'"
                    >mdi mdi-video-off</v-icon
                  >
                  NO VIDEO
                </div>
              </div>
            </div>
          </v-card>
          <v-card-text
            v-if="displayMode === 'detailed'"
            class="pa-2 text-caption"
          >
            <v-row class="d-flex align-center">
              <v-col>
                <div class="text-right" v-if="showLocation">
                  <v-btn
                    small
                    v-if="thumbnail.tasks && thumbnail.tasks.length > 0"
                    @click="openTaskDialog(thumbnail)"
                  >
                    <v-icon v-if="isOverdue(thumbnail)" color="warning"
                      >mdi-alert</v-icon
                    >
                    View Tasks
                  </v-btn>
                  <div v-if="isOverdue(thumbnail)" class="red--text">
                    Overdue!
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-col>
      </v-row>
    </div>

    <div
      v-if="!loading"
      class="d-flex justify-space-between align-center mt-2 pagination-container"
    >
      <v-btn
        icon
        :disabled="
          !paginationTracking.hasMore.newer || loading || isFetchingIncidents
        "
        @click="prevPage"
        class="pagination-nav-btn"
      >
        <v-icon>fas fa-chevron-left</v-icon>
      </v-btn>
      <v-btn
        icon
        :disabled="
          !paginationTracking.hasMore.older || loading || isFetchingIncidents
        "
        @click="nextPage"
        class="pagination-nav-btn"
      >
        <v-icon>fas fa-chevron-right</v-icon>
      </v-btn>
    </div>

    <DialogLayout title="Tasks" v-model="taskDialog" width="1200">
      <CardIncidentTasks :incident="selectedIncident" />
    </DialogLayout>
  </div>
</template>

<script>
import { EEN_EVENT_IMAGES } from "./graphql";
import { INCIDENT_HISTORY } from "../reports/graphql";
import { GET_ALL_ZONE_DEVICES } from "../dashboard/visual_verify_enterprise/graphql";
import useFacility from "../authentication/useFacility";
import DateTimeFilter from "@tod-ui/components/DateTimeFilter.vue";
import EventVideoDialog from "./EventVideoDialog.vue";
import { computed } from "vue";
import { formatIncidentType } from "@components/common/helpers/incident";
import CardIncidentTasks from "../incident/CardIncidentTasks.vue";
import DialogLayout from "@layout/DialogLayout.vue";

export default {
  name: "VideoGridIncidents",
  components: {
    DateTimeFilter,
    EventVideoDialog,
    CardIncidentTasks,
    DialogLayout,
  },
  props: {
    itemsPerPage: {
      type: Number,
      default: 6,
    },
    displayMode: {
      type: String,
      default: "detailed",
      validator: (value) => ["detailed", "compact"].includes(value),
    },
    showLocation: {
      type: Boolean,
      default: false,
    },
    parentId: {
      type: String,
      default: null,
    },
    selectedFacilities: {
      type: Array,
      default: () => [],
    },
    useFacilityFiltering: {
      type: Boolean,
      default: false,
    },
    externalDateRange: {
      type: Object,
      default: null,
    },
    hideDateFilter: {
      type: Boolean,
      default: false,
    },
    incidentFilter: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const {
      currentFacilityId,
      facilityFormatDateTime4Humans,
      facilityTimeZone,
    } = useFacility();
    const effectiveOrganizationId = computed(() => {
      return props.parentId || currentFacilityId.value;
    });

    return {
      currentFacilityId,
      facilityTimeZone,
      facilityFormatDateTime4Humans,
      effectiveOrganizationId,
    };
  },
  data() {
    return {
      initialLoadComplete: false,
      loading: true,
      isFetchingIncidents: false,
      dateRange: { after: undefined, before: undefined },
      incidents: [],
      allImageRequests: [],
      thumbnails: [],
      currentPage: 0,
      loadedPages: 0,
      allRequestsLoaded: false,
      eventsDialog: false,
      currentThumbnail: null,
      showTasks: false,
      taskDialog: false,
      selectedIncident: null,
      organizationIds: [],
      paginationTracking: {
        minSeqId: null,
        maxSeqId: null,
        seqIdLt: null,
        requestKey: null,
        hasMore: {
          newer: false,
          older: false,
        },
        historyStack: [],
        pageCache: {},
      },
      isLoading: false,
    };
  },

  computed: {
    gridCols() {
      return {
        cols: 12,
        sm: 6,
        md: 3,
      };
    },
    totalPages() {
      if (this.allRequestsLoaded) {
        return Math.ceil(this.thumbnails.length / this.itemsPerPage) || 1;
      }
      if (this.allImageRequests.length > 0) {
        return Math.ceil(this.allImageRequests.length / this.itemsPerPage);
      }

      return Math.max(
        this.loadedPages + 1,
        Math.ceil(this.thumbnails.length / this.itemsPerPage)
      );
    },

    visibleThumbnails() {
      const start = this.currentPage * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      const seen = new Set();
      const unique = this.thumbnails.filter((thumb) => {
        if (seen.has(thumb.id)) return false;
        seen.add(thumb.id);
        return true;
      });

      return unique.slice(start, end);
    },
  },
  watch: {
    dateRange(newRange, oldRange) {
      if (!this.initialLoadComplete) {
        this.initialLoadComplete = true;
        this.resetPagination();
        this.fetchIncidents();
      } else if (
        !this.isFetchingIncidents &&
        (newRange.after !== oldRange.after ||
          newRange.before !== oldRange.before)
      ) {
        this.resetPagination();
        this.fetchIncidents();
      }
    },
    parentId: {
      handler(newValue) {
        if (newValue) {
          this.fetchIncidents();
        }
      },
    },
    selectedFacilities: {
      handler() {
        // The set of zones (and so the incident scope) is derived from the
        // selected facilities, so rediscover zones rather than refetching
        // incidents against a stale organization list.
        if (this.useFacilityFiltering) {
          this.resetPagination();
          this.loadZoneDevices();
        } else if (this.initialLoadComplete && !this.isFetchingIncidents) {
          this.fetchIncidents();
        }
      },
      deep: true,
    },
    externalDateRange: {
      immediate: true,
      handler(newRange) {
        if (newRange) {
          this.dateRange = { ...newRange };

          if (this.initialLoadComplete && !this.isFetchingIncidents) {
            this.resetPagination();
            this.fetchIncidents();
          }
        }
      },
      deep: true,
    },
    incidentFilter: {
      handler() {
        if (this.initialLoadComplete && !this.isFetchingIncidents) {
          this.fetchIncidents();
        }
      },
      deep: true,
    },
  },

  methods: {
    getFacilityIds() {
      if (this.useFacilityFiltering && this.selectedFacilities?.length > 0) {
        return this.selectedFacilities.map((facility) => facility.id);
      } else if (this.parentId) {
        return [this.parentId];
      } else if (this.currentFacilityId) {
        return [this.currentFacilityId];
      }
      return [];
    },
    gotoZone(zoneId) {
      this.$router.push({
        name: "ViewSmartZone",
        params: {
          smart_zone_id: zoneId,
        },
      });
    },

    gotoIncidentPage(incident) {
      this.$router.push({
        name: "Incident",
        params: {
          organization_id: incident.organization.ancestors
            ? incident.organization.ancestors[0].id
            : this.currentFacilityId,
          incident_id: incident.id,
        },
      });
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      return this.facilityFormatDateTime4Humans(
        date.toISOString(),
        false,
        true,
        true
      );
    },
    isOverdue(thumbnail) {
      if (!thumbnail || !thumbnail.tasks) {
        return false;
      }
      return thumbnail.tasks.some(
        (task) =>
          new Date(task.dueAt) < new Date() && task.resolution !== "completed"
      );
    },
    async fetchIncidents() {
      if (this.isFetchingIncidents) {
        return;
      }
      try {
        this.isFetchingIncidents = true;
        this.loading = true;
        this.thumbnails = [];
        this.allImageRequests = [];
        this.currentPage = 0;
        this.loadedPages = 0;
        this.allRequestsLoaded = false;

        if (
          this.useFacilityFiltering &&
          (!this.selectedFacilities || this.selectedFacilities.length === 0)
        ) {
          this.allRequestsLoaded = true;
          this.$emit("no-incidents");
          return;
        }

        let parentIds = this.getFacilityIds();

        // Create a unique cache key
        const parentIdStr = this.parentId || this.currentFacilityId || "";
        const facilitiesStr =
          this.useFacilityFiltering && this.selectedFacilities
            ? this.selectedFacilities.map((f) => f.id).join(",")
            : parentIdStr;
        const zoneIdsStr = this.organizationIds.join(",");

        const cacheKey = `${this.paginationTracking.seqIdLt || "initial"}-${
          this.dateRange.after || ""
        }-${this.dateRange.before || ""}-${JSON.stringify(
          this.incidentFilter
        )}-${facilitiesStr}-${zoneIdsStr}`;

        // Use cache if available
        if (this.paginationTracking.pageCache[cacheKey]) {
          this.incidents = [
            ...this.paginationTracking.pageCache[cacheKey].incidents,
          ];
          this.thumbnails = [
            ...this.paginationTracking.pageCache[cacheKey].thumbnails,
          ];
          this.allImageRequests = [
            ...this.paginationTracking.pageCache[cacheKey].requests,
          ];
          this.paginationTracking.minSeqId =
            this.paginationTracking.pageCache[cacheKey].minSeqId;
          this.paginationTracking.maxSeqId =
            this.paginationTracking.pageCache[cacheKey].maxSeqId;
          this.paginationTracking.hasMore = {
            ...this.paginationTracking.pageCache[cacheKey].hasMore,
          };
          this.paginationTracking.requestKey = cacheKey;
          this.allRequestsLoaded = true;
          this.loading = false;
          return;
        }

        if (
          parentIds &&
          parentIds.length > 0 &&
          this.organizationIds &&
          this.organizationIds.length > 0
        ) {
          const queryVariables = {
            // Scope by the facility list (`parentIds`) only. A singular
            // `parentId` is ANDed with it server-side, so a default-org root
            // would filter away every selected facility outside that org — the
            // cross-region case.
            organizationSubtypes: ["zone"], // Fixed to zones since location filter is disabled
            dispositions:
              this.incidentFilter.dispositions?.length > 0
                ? this.incidentFilter.dispositions
                : ["open", "in_progress", "in_review"],
            dateTimeFilter: this.dateRange,
            limit: this.itemsPerPage,
            seqIdLt: this.paginationTracking.seqIdLt,
            parentIds: parentIds,
            organizationIds: this.organizationIds,
          };

          if (
            this.incidentFilter.types &&
            this.incidentFilter.types.length > 0
          ) {
            queryVariables.types = this.incidentFilter.types;
          }
          // Add after hours filtering
          if (this.incidentFilter.duringAccessHours !== undefined) {
            queryVariables.duringAccessHours =
              this.incidentFilter.duringAccessHours;
          }
          if (
            this.incidentFilter.severity &&
            this.incidentFilter.severity.length > 0
          ) {
            queryVariables.priorities = this.incidentFilter.severity;
          } else {
            queryVariables.priorities = [
              "critical",
              "severe",
              "moderate",
              "substantial",
              "low",
            ];
          }

          if (this.incidentFilter.isOverdue) {
            queryVariables.overdue = true;
          }
          const {
            severity,
            isOverdue,
            types,
            duringAccessHours,
            ...otherFilters
          } = this.incidentFilter;

          const { data } = await this.$apollo.query({
            query: INCIDENT_HISTORY,
            variables: {
              ...queryVariables,
              ...otherFilters,
            },
            fetchPolicy: "network-only",
          });

          // Store the request key for cache lookups
          this.paginationTracking.requestKey = cacheKey;

          const filteredIncidents = (data.incidents || []).filter(
            (incident) =>
              incident.type !== "humidity" && incident.type !== "temperature"
          );

          this.incidents = filteredIncidents;

          this.paginationTracking.hasMore.newer =
            this.paginationTracking.historyStack.length > 0;
          this.paginationTracking.hasMore.older =
            this.paginationTracking.historyStack.length < 0;

          if (this.incidents.length > 0) {
            const sortedBySeqId = [...this.incidents].sort(
              (a, b) => a.seqId - b.seqId
            );

            this.paginationTracking.minSeqId = sortedBySeqId[0].seqId;
            this.paginationTracking.maxSeqId =
              sortedBySeqId[sortedBySeqId.length - 1].seqId;
            this.paginationTracking.hasMore.older =
              this.incidents.length >= this.itemsPerPage;

            await this.prepareImageRequests();

            if (this.allImageRequests.length > 0) {
              const visibleRequests = this.allImageRequests.slice(
                0,
                this.itemsPerPage
              );
              await this.loadFirstBatch(visibleRequests);

              // Store in cache
              this.paginationTracking.pageCache[cacheKey] = {
                incidents: [...this.incidents],
                thumbnails: [...this.thumbnails],
                requests: [...this.allImageRequests],
                minSeqId: this.paginationTracking.minSeqId,
                maxSeqId: this.paginationTracking.maxSeqId,
                hasMore: { ...this.paginationTracking.hasMore },
              };
            }
          } else {
            this.allRequestsLoaded = true;
            this.paginationTracking.pageCache[cacheKey] = {
              incidents: [],
              thumbnails: [],
              requests: [],
              minSeqId: null,
              maxSeqId: null,
              hasMore: {
                newer: this.paginationTracking.historyStack.length > 0,
                older: false,
              },
            };
            this.$emit("no-incidents");
          }
        } else {
          this.allRequestsLoaded = true;
          this.$emit("no-incidents");
        }
      } catch (error) {
        console.error("Error in fetchIncidents:", error);
        this.allRequestsLoaded = true;
        this.paginationTracking.hasMore.newer =
          this.paginationTracking.historyStack.length > 0;
        this.$emit("no-incidents");
      } finally {
        this.loading = false;
        this.isFetchingIncidents = false;
      }
    },

    async loadFirstBatch(batchRequests) {
      if (!batchRequests || batchRequests.length === 0) {
        this.allRequestsLoaded = true;
        this.loading = false;
        return;
      }

      try {
        // splitting api calls for 30 grid
        if (this.itemsPerPage === 30) {
          this.thumbnails = batchRequests.map((req, index) => ({
            ...req.incidentData,
            uniqueId: `${req.incidentData.id}-${Date.now()}-${index}`,
            isLoading: true,
            thumbnail: null,
          }));

          this.loading = false;

          const BATCH_SIZE = 6;
          const batchPromises = [];

          for (let i = 0; i < batchRequests.length; i += BATCH_SIZE) {
            const batchSlice = batchRequests.slice(i, i + BATCH_SIZE);
            batchPromises.push(this.processBatch(batchSlice, i));
          }

          await Promise.all(batchPromises);
        } else {
          this.loading = true;
          const cleanRequests = batchRequests.map((req) => req.apiRequest);

          const { data } = await this.$apollo.query({
            query: EEN_EVENT_IMAGES,
            variables: {
              imageRequests: cleanRequests,
            },
            fetchPolicy: "network-only",
          });

          const newThumbnails = data?.eenEventImages || [];

          const enhancedThumbnails = newThumbnails.map((thumbnail, index) => {
            return {
              ...thumbnail,
              ...batchRequests[index].incidentData,
              uniqueId: `${
                batchRequests[index].incidentData.id
              }-${Date.now()}-${index}`,
            };
          });

          this.thumbnails = enhancedThumbnails;
        }
        this.loadedPages = 1;
        this.allRequestsLoaded = true;
      } catch (error) {
        console.error("Error loading thumbnails:", error);
        this.allRequestsLoaded = true;
      } finally {
        this.loading = false;
      }
    },
    async processBatch(batchRequests, startIndex) {
      try {
        const cleanRequests = batchRequests.map((req) => req.apiRequest);

        const { data } = await this.$apollo.query({
          query: EEN_EVENT_IMAGES,
          variables: {
            imageRequests: cleanRequests,
          },
          fetchPolicy: "network-only",
        });

        const newThumbnails = data?.eenEventImages || [];

        // Update each thumbnail in this batch individually

        newThumbnails.forEach((thumbnail, index) => {
          const globalIndex = startIndex + index;
          if (globalIndex < this.thumbnails.length) {
            this.$set(this.thumbnails, globalIndex, {
              ...this.thumbnails[globalIndex],
              ...thumbnail,
              isLoading: false,
            });
          }
        });

        return true;
      } catch (error) {
        console.error(`Error loading batch at index ${startIndex}:`, error);

        // Mark these thumbnails as failed but preserve their position
        batchRequests.forEach((_, index) => {
          const globalIndex = startIndex + index;
          if (globalIndex < this.thumbnails.length) {
            this.$set(this.thumbnails, globalIndex, {
              ...this.thumbnails[globalIndex],
              isLoading: false,
              loadError: true,
            });
          }
        });

        return false;
      }
    },

    async prepareImageRequests() {
      const eenIncidents = this.incidents.filter((incident) => {
        if (!incident.organization?.devices) return false;

        const hasEenDevice = incident.organization.devices.some(
          (device) => device.type === "een"
        );

        if (!hasEenDevice) return false;

        return true;
      });

      const requests = [];

      eenIncidents.forEach((incident) => {
        const eenDevices = incident.organization.devices.filter(
          (device) => device.type === "een"
        );

        eenDevices.forEach((device) => {
          let formattedTimestamp = incident.startedAt;

          try {
            const date = new Date(incident.startedAt);
            if (!isNaN(date.getTime())) {
              formattedTimestamp = date.toISOString();
            }
          } catch (e) {
            console.error("Error formatting timestamp:", e);
          }

          const endedAt = incident.closedAt;
          const organizationId = incident.organization.ancestors
            ? incident.organization.ancestors[0].id
            : this.currentFacilityId;

          const apiRequest = {
            actorId: device.externalId,
            organizationId: organizationId,
            startTimestamp: formattedTimestamp,
          };

          requests.push({
            apiRequest: apiRequest,
            incidentData: {
              actorId: device.externalId,
              organizationId: incident.organization.ancestors
                ? incident.organization.ancestors[0].id
                : this.currentFacilityId,
              startTimestamp: formattedTimestamp,
              priority: incident?.tasks[0]?.priority,
              incidentType: formatIncidentType(incident),
              deviceLocation: incident?.devices[0]?.location,
              endedAt: endedAt,
              zoneName: incident.organization.name,
              id: incident.id,
              type: incident.type,
              subtype: incident.subtype,
              violatedThreshold: incident.violatedThreshold,
              lowThreshold: incident.lowThreshold,
              highThreshold: incident.highThreshold,
              isVacant: incident.isVacant,
              extendedDuration: incident.extendedDuration,
              duringAccessHours: incident.duringAccessHours,
              hasDisabledLienResponder: incident.hasDisabledLienResponder,
              queuedUntilDawn: incident.queuedUntilDawn,
              bypassedAccess: incident.bypassedAccess,
              resolution: incident.resolution,
              tasks: incident.tasks,
              events: incident.events,
              organization: incident.organization,
              seqId: incident.seqId,
            },
          });
        });
      });

      this.allImageRequests = requests;
      if (requests.length <= this.itemsPerPage) {
        this.allRequestsLoaded = true;
      } else {
        this.allRequestsLoaded = false;
      }
    },

    loadNextIncidents() {
      if (this.paginationTracking.historyStack.length === 0) {
        return;
      }
      this.loading = true;
      const previousState = this.paginationTracking.historyStack.pop();

      this.paginationTracking.seqIdLt = previousState.seqIdLt;
      this.currentPage = 0;

      // Instead of fetching again, use the cache
      this.fetchIncidents();
    },

    loadPreviousIncidents() {
      if (!this.paginationTracking.minSeqId) return;

      // Save current state to history stack
      this.paginationTracking.historyStack.push({
        seqIdLt: this.paginationTracking.seqIdLt,
        minSeqId: this.paginationTracking.minSeqId,
        maxSeqId: this.paginationTracking.maxSeqId,
        requestKey: this.paginationTracking.requestKey,
      });

      this.loading = true;

      // Update the seqId to get older incidents
      this.paginationTracking.seqIdLt = this.paginationTracking.minSeqId;
      this.currentPage = 0;

      // check cache first
      this.fetchIncidents();
    },

    prevPage() {
      if (this.currentPage > 0) {
        this.currentPage--;
      } else if (this.paginationTracking.hasMore.newer) {
        this.loadNextIncidents();
      }
    },

    nextPage() {
      const nextPageIndex = this.currentPage + 1;

      if (nextPageIndex < this.totalPages) {
        this.currentPage = nextPageIndex;
      } else if (this.paginationTracking.hasMore.older) {
        this.loadPreviousIncidents();
      }
    },

    showEventsDialog(thumbnail) {
      this.currentThumbnail = thumbnail;
      this.eventsDialog = true;
    },
    handleDialogClosed() {
      this.currentThumbnail = null;
    },
    openTaskDialog(thumbnail) {
      this.selectedIncident = thumbnail;
      this.taskDialog = true;
    },
    resetPagination() {
      this.paginationTracking = {
        minSeqId: null,
        maxSeqId: null,
        seqIdLt: null,
        requestKey: null,
        hasMore: {
          newer: false,
          older: false,
        },
        historyStack: [],
        pageCache: {},
      };
      this.thumbnails = [];
      this.allImageRequests = [];
      this.currentPage = 0;
      this.loadedPages = 0;
      this.allRequestsLoaded = false;
      this._currentImageCacheKey = null;
    },
    // Discover the zone organizations that scope the incident query. `devices`
    // accepts a single `parentId`, so in facility-filtering mode each selected
    // facility is queried and merged — a single default-org root would miss
    // zones in a cross-region manager's other regions. Without facility
    // filtering the facility context's own subtree is used.
    async loadZoneDevices() {
      try {
        if (
          this.useFacilityFiltering &&
          (!this.selectedFacilities || this.selectedFacilities.length === 0)
        ) {
          this.organizationIds = [];
          this.loading = false;
          this.allRequestsLoaded = true;
          this.$emit("no-incidents");
          return;
        }

        let organizationIds = [];

        if (this.useFacilityFiltering) {
          const facilityIds = this.selectedFacilities.map(
            (facility) => facility.id
          );
          const results = await Promise.all(
            facilityIds.map((parentId) =>
              this.$apollo.query({
                query: GET_ALL_ZONE_DEVICES,
                variables: { parentId, type: "een" },
                fetchPolicy: "network-only",
              })
            )
          );
          const devices = results.flatMap(({ data }) => data?.devices || []);
          const selectedFacilityIds = new Set(facilityIds);
          organizationIds = devices
            .filter((device) => {
              const facilityAncestor = device.organization.ancestors.find(
                (ancestor) => selectedFacilityIds.has(ancestor.id)
              );
              return (
                !!facilityAncestor && device.organization.subtype === "zone"
              );
            })
            .map((device) => device.organization.id);
        } else {
          const parentId = this.parentId || this.currentFacilityId;
          if (!parentId) {
            this.loading = false;
            this.allRequestsLoaded = true;
            this.$emit("no-incidents");
            return;
          }
          const { data } = await this.$apollo.query({
            query: GET_ALL_ZONE_DEVICES,
            variables: { parentId, type: "een" },
            fetchPolicy: "network-only",
          });
          organizationIds = (data?.devices || [])
            .filter((device) => device.organization.subtype === "zone")
            .map((device) => device.organization.id);
        }

        this.organizationIds = [...new Set(organizationIds)];

        if (this.organizationIds.length > 0 || this.parentId) {
          this.triggerFetchFromApollo();
        } else {
          this.loading = false;
          this.allRequestsLoaded = true;
          this.$emit("no-incidents");
        }
      } catch (error) {
        console.error("Error fetching zone devices:", error);
        this.loading = false;
        this.allRequestsLoaded = true;
      }
    },
    triggerFetchFromApollo() {
      if (!this.initialLoadComplete) {
        this.initialLoadComplete = true;
        this.fetchIncidents();
      } else if (!this.isFetchingIncidents) {
        this.fetchIncidents();
      }
    },
    getCardHeight() {
      return "200px";
    },
  },
  created() {
    this.initialLoadComplete = false;
    this.isFetchingIncidents = false;
    this.loadZoneDevices();
  },
};
</script>
<style scoped>
/* Grid container fixes */
.video-grid-row {
  margin: -4px; /* Negative margin to counteract col padding */
}

.video-grid-col {
  padding: 4px !important; /* Consistent padding for all columns */
}

.thumbnail-card {
  transition: transform 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
  margin: 0; /* Remove any default margins */
  width: 100%;
}

.thumbnail-card:hover {
  transform: scale(1.02);
}

.video-container {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  width: 100%;
  height: 100%;
}

.video-image {
  transition: transform 0.3s ease;
  border-radius: 8px; /* Ensure image itself has rounded corners */
}

.video-container:hover .video-image {
  transform: scale(1.02);
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 2;
  opacity: 1;
  transition: opacity 0.3s ease;
  border-radius: 8px; /* Add border radius to overlay */
}

.video-container:hover .video-overlay {
  opacity: 0.8;
}

.play-button {
  transition: transform 0.2s ease, color 0.2s ease;
}

.video-container:hover .play-button {
  transform: scale(1.1);
  color: #ff4136;
}

.video-container:hover .play-button .v-icon {
  color: #ff4136;
}

/* Alternative approach if the above doesn't work */
.video-container:hover .play-button i {
  color: #ff4136;
}

/* Always visible text overlay that fades down on hover */
.text-overlay-always-visible {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 16px 12px 12px;
  color: white;
  transform: translateY(0); /* Always visible */
  transition: transform 0.3s ease, opacity 0.3s ease;
  z-index: 3;
  opacity: 1;
  border-radius: 0 0 8px 8px; /* Round only bottom corners */
}

/* On hover, text fades down and out */
.video-container:hover .text-overlay-always-visible {
  transform: translateY(20px); /* Moves down 20px */
  opacity: 0; /* Fades out but doesn't disappear completely */
}

.text-content {
  font-size: 12px;
  line-height: 1.3;
}

.zone-name {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.zone-name .clickable-link {
  color: white;
  text-decoration: none;
  transition: color 0.2s ease;
}

.zone-name .clickable-link:hover {
  color: #ff4136;
}

.incident-status {
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 3px;
  color: #ffd700; /* Yellow color for incident type */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.timestamp {
  font-size: 11px;
  margin-bottom: 3px;
  opacity: 0.9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.timestamp .clickable-link {
  color: white;
  text-decoration: none;
  transition: color 0.2s ease;
}

.timestamp .clickable-link:hover {
  color: #0084ff;
}

.location {
  font-size: 10px;
  margin-bottom: 2px;
  opacity: 0.8;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-video-text {
  color: white;
  background-color: rgba(0, 0, 0, 0.5);
  font-weight: bold;
  border-radius: 4px;
  font-size: 0.9rem;
  letter-spacing: 1px;
}

.fill-height {
  height: 100%;
}

.compact-card {
  margin-bottom: 6px;
}

.compact-card .no-video-text {
  font-size: 0.7rem;
  padding: 2px 6px !important;
}

.red--text {
  color: red !important;
  font-weight: bold;
  animation: pulse 1.5s infinite alternate;
}

@keyframes pulse {
  from {
    opacity: 0.7;
  }
  to {
    opacity: 1;
  }
}

/* Pagination styling */
.pagination-container {
  margin-top: 12px;
  padding: 8px 0;
  border-top: 1px solid #eee;
}

.pagination-nav-btn {
  background-color: #f5f5f5;
  border-radius: 50%;
  height: 36px;
  width: 36px;
}

.pagination-nav-btn:hover:not([disabled]) {
  background-color: #e0e0e0;
}

.pagination-indicator {
  font-weight: 500;
  min-width: 60px;
  text-align: center;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .text-content {
    font-size: 11px;
  }

  .zone-name {
    font-size: 13px;
  }

  .incident-status {
    font-size: 11px;
  }

  .timestamp {
    font-size: 10px;
  }

  .location {
    font-size: 9px;
  }
}

@media (max-width: 960px) {
  .video-container {
    height: 180px !important;
  }

  .text-overlay-always-visible {
    padding: 12px 10px 10px;
  }
}

@media (max-width: 600px) {
  .video-container {
    height: 160px !important;
  }

  .text-overlay-always-visible {
    padding: 10px 8px 8px;
  }
}
</style>
